import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CART_STORAGE_KEY } from '../constants';
import { addCartItem, deleteCartItem, ensureCart } from '../api';

const CartContext = createContext({
  cart: null,
  loading: true,
  addItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
  refresh: () => Promise.resolve()
});

const getStoredToken = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(CART_STORAGE_KEY);
};

const storeToken = token => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CART_STORAGE_KEY, token);
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => getStoredToken());

  const syncCart = useCallback(async () => {
    setLoading(true);
    try {
      const storedToken = getStoredToken();
      const { cart: fetchedCart, token: ensuredToken } = await ensureCart(storedToken);
      if (ensuredToken !== storedToken) {
        storeToken(ensuredToken);
        setToken(ensuredToken);
      }
      setCart(fetchedCart);
    } catch (error) {
      console.error('Unable to load cart', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const requireToken = useCallback(async () => {
    let currentToken = getStoredToken();
    if (!currentToken) {
      const { cart: newCart, token: newToken } = await ensureCart(null);
      storeToken(newToken);
      setToken(newToken);
      setCart(newCart);
      return newToken;
    }
    return currentToken;
  }, []);

  const addItem = useCallback(
    async (payload) => {
      const activeToken = await requireToken();
      await addCartItem(activeToken, payload);
      await syncCart();
    },
    [requireToken, syncCart]
  );

  const removeItem = useCallback(
    async (itemId) => {
      const currentToken = getStoredToken();
      if (!currentToken) return;
      await deleteCartItem(currentToken, itemId);
      await syncCart();
    },
    [syncCart]
  );

  useEffect(() => {
    syncCart();
  }, [syncCart]);

  const value = useMemo(() => ({
    cart,
    cartToken: token,
    loading,
    addItem,
    removeItem,
    refresh: syncCart
  }), [cart, token, loading, addItem, removeItem, syncCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
